/**
 * Seller Financing Analysis Component
 *
 * Displays seller financing details including:
 * - Deal terms
 * - Payment schedule
 * - Amortization breakdown
 * - Risk assessment
 */

import React, { useState } from 'react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

function SellerFinancingAnalysis({ sellerFinancingAnalysis }) {
  const [showFullSchedule, setShowFullSchedule] = useState(false);

  if (!sellerFinancingAnalysis) return null;

  const { dealTerms, payments, sellerPerspective, yearlyBreakdown } = sellerFinancingAnalysis;

  const getRiskColor = (level) => {
    switch (level) {
      case 'low': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'high': return '#ef4444';
      default: return '#6b7280';
    }
  };

  return (
    <div className="seller-financing-analysis">
      <h2>Seller Financing Analysis</h2>
      <p className="section-description">
        Detailed analysis of seller note terms, payment schedule, and risk assessment
      </p>

      {/* Deal Terms Summary */}
      <div className="deal-terms-summary">
        <h3>Deal Terms</h3>
        <div className="terms-grid">
          <div className="term-item">
            <span className="term-label">Total Price</span>
            <span className="term-value">{formatCurrency(dealTerms.totalPrice)}</span>
          </div>
          <div className="term-item">
            <span className="term-label">Down Payment</span>
            <span className="term-value">{formatCurrency(dealTerms.downPayment)}</span>
          </div>
          <div className="term-item">
            <span className="term-label">Seller Note</span>
            <span className="term-value">{formatCurrency(dealTerms.noteAmount)}</span>
          </div>
          <div className="term-item">
            <span className="term-label">Interest Rate</span>
            <span className="term-value">{(dealTerms.interestRate * 100).toFixed(1)}%</span>
          </div>
          <div className="term-item">
            <span className="term-label">Term</span>
            <span className="term-value">{dealTerms.termYears} years</span>
          </div>
          <div className="term-item">
            <span className="term-label">LTV Ratio</span>
            <span className="term-value">{dealTerms.ltvRatio}%</span>
          </div>
        </div>
      </div>

      {/* Payment Summary */}
      <div className="payment-summary">
        <h3>Payment Schedule</h3>
        <div className="payment-cards">
          <div className="payment-card">
            <span className="card-label">Monthly Payment</span>
            <span className="card-value">{formatCurrency(payments.monthlyPayment)}</span>
          </div>
          <div className="payment-card">
            <span className="card-label">Annual Payment</span>
            <span className="card-value">{formatCurrency(payments.annualPayment)}</span>
          </div>
          <div className="payment-card">
            <span className="card-label">Total Payments</span>
            <span className="card-value">{formatCurrency(payments.totalPayments)}</span>
          </div>
          <div className="payment-card interest">
            <span className="card-label">Total Interest</span>
            <span className="card-value">{formatCurrency(payments.totalInterest)}</span>
          </div>
        </div>
      </div>

      {/* Seller Perspective */}
      <div className="seller-perspective">
        <h3>Seller Perspective</h3>
        <div className="perspective-metrics">
          <div className="metric">
            <span className="label">Cash at Close</span>
            <span className="value">{formatCurrency(sellerPerspective.cashAtClose)}</span>
          </div>
          <div className="metric">
            <span className="label">Note Receivable</span>
            <span className="value">{formatCurrency(sellerPerspective.noteReceivable)}</span>
          </div>
          <div className="metric">
            <span className="label">Total Proceeds</span>
            <span className="value highlight">{formatCurrency(sellerPerspective.totalProceeds)}</span>
            <span className="subtext">Including interest income</span>
          </div>
          <div className="metric">
            <span className="label">Present Value</span>
            <span className="value">{formatCurrency(sellerPerspective.presentValue)}</span>
            <span className="subtext">Discounted for risk</span>
          </div>
        </div>

        <div className="risk-assessment" style={{ borderLeftColor: getRiskColor(sellerPerspective.riskLevel) }}>
          <div className="risk-header">
            <span className="risk-label">Risk Assessment</span>
            <span className={`risk-badge ${sellerPerspective.riskLevel}`}>
              {sellerPerspective.riskLevel.toUpperCase()} RISK
            </span>
          </div>
          <p className="risk-recommendation">{sellerPerspective.recommendation}</p>
        </div>
      </div>

      {/* Amortization Schedule */}
      <div className="amortization-schedule">
        <h3>
          Amortization Schedule
          <button
            className="toggle-btn"
            onClick={() => setShowFullSchedule(!showFullSchedule)}
          >
            {showFullSchedule ? 'Show Summary' : 'Show Full Schedule'}
          </button>
        </h3>

        <table className="schedule-table">
          <thead>
            <tr>
              <th>Year</th>
              <th>Principal Paid</th>
              <th>Interest Paid</th>
              <th>Ending Balance</th>
            </tr>
          </thead>
          <tbody>
            {(showFullSchedule ? yearlyBreakdown : yearlyBreakdown.slice(0, 3)).map((year) => (
              <tr key={year.year}>
                <td>Year {year.year}</td>
                <td>{formatCurrency(year.principalPaid)}</td>
                <td>{formatCurrency(year.interestPaid)}</td>
                <td>{formatCurrency(year.endingBalance)}</td>
              </tr>
            ))}
            {!showFullSchedule && yearlyBreakdown.length > 3 && (
              <tr className="ellipsis-row">
                <td colSpan="4">
                  ... {yearlyBreakdown.length - 3} more years
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td><strong>Total</strong></td>
              <td><strong>{formatCurrency(dealTerms.noteAmount)}</strong></td>
              <td><strong>{formatCurrency(payments.totalInterest)}</strong></td>
              <td><strong>$0</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Visual Breakdown */}
      <div className="visual-breakdown">
        <h3>Principal vs Interest Over Time</h3>
        <div className="stacked-bar-chart">
          {yearlyBreakdown.map((year) => {
            const total = year.principalPaid + year.interestPaid;
            const principalPercent = (year.principalPaid / total) * 100;
            const interestPercent = (year.interestPaid / total) * 100;

            return (
              <div className="bar-group" key={year.year}>
                <div className="bar-stack">
                  <div
                    className="bar-segment principal"
                    style={{ height: `${principalPercent}%` }}
                    title={`Principal: ${formatCurrency(year.principalPaid)}`}
                  ></div>
                  <div
                    className="bar-segment interest"
                    style={{ height: `${interestPercent}%` }}
                    title={`Interest: ${formatCurrency(year.interestPaid)}`}
                  ></div>
                </div>
                <span className="bar-label">Y{year.year}</span>
              </div>
            );
          })}
        </div>
        <div className="chart-legend">
          <div className="legend-item">
            <span className="legend-color principal"></span>
            <span>Principal</span>
          </div>
          <div className="legend-item">
            <span className="legend-color interest"></span>
            <span>Interest</span>
          </div>
        </div>
      </div>

      {/* Seller Financing Tips */}
      <div className="financing-tips">
        <h3>Seller Note Considerations</h3>
        <ul>
          <li>
            <strong>Security Interest:</strong> Ensure the note is secured by the business assets
            or personal guarantee from the buyer
          </li>
          <li>
            <strong>Default Provisions:</strong> Include clear default triggers and remedies
            in the promissory note
          </li>
          <li>
            <strong>Subordination:</strong> Understand how your note ranks relative to any
            senior debt the buyer may have
          </li>
          <li>
            <strong>Prepayment:</strong> Consider whether to allow prepayment and whether
            to include a prepayment penalty
          </li>
          <li>
            <strong>Tax Implications:</strong> Interest income will be taxed as ordinary income;
            consult with your tax advisor
          </li>
        </ul>
      </div>
    </div>
  );
}

export default SellerFinancingAnalysis;
